/**
 * A JELSZÓ-SZABÁLY KLIENS-OLDALI TÜKRE [K-3, 2026-09-16 · javítva K-7, 2026-09-16].
 *
 * A SZERVER szabálya: `backend/PinyoApi/DTOs/PasswordPolicy.cs` (`Pattern` + `Message`) — az
 * ott lévő `[RegularExpression]` mind a négy jelszó-úton (regisztráció, admin-létrehozás,
 * jelszó-csere, „elfelejtett jelszó" utáni visszaállítás) ugyanez:
 *
 *     ^(?=.*\p{Ll})(?=.*\p{Lu})(?=.*\d)(?=.*[^\p{L}\p{N}\s])\S+\z
 *
 * A MAI SZABÁLY: legalább 6 karakter, kell kisbetű, nagybetű, számjegy és legalább egy NEM
 * betű/szám karakter (bármi: `! ? - _ . , : ; #` …), és NEM lehet benne szóköz.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * AZ INVARIÁNS: A KLIENS SOHA NEM LEHET SZIGORÚBB A SZERVERNÉL
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * Ha a kliens szigorúbb, a felhasználó ITT akad el, és a szerver (helyes) mondata el sem jut
 * hozzá — a hiba pedig javíthatatlannak tűnik. Lazább LEHET: a végső őr a szerver, ott a
 * rossz jelszó 400-at kap a teljes szabályt kimondó magyar mondattal.
 *
 * MI VOLT A BAJ (MÉRVE 2026-09-16, a .NET regexet PowerShellből futtatva): a korábbi tükör
 * `\p{...}` NÉLKÜL, ASCII-eszközökkel dolgozott, és 7 ponton eltért a szervertől — ebből
 * KETTŐ az invariánst SÉRTETTE, vagyis a kliens volt a szigorúbb:
 *   • NEM ASCII SZÁMJEGY: a .NET `\d` = `\p{Nd}`, tehát `Aa٣!xy` (arab-indiai 3),
 *     `Aa３!xy` (teljes szélességű 3), `Aa३!xy` (dévanágari 3) a SZERVERNEK ÉRVÉNYES
 *     (mérve: 201 Created), a kliens ASCII-only `isDigit`-je viszont ELUTASÍTOTTA;
 *   • `U+FEFF` (ZWNBSP): a JS `\s` ILLESZTI, a .NET `\s` NEM — `Aa1!x` + `U+FEFF` a
 *     szervernek érvényes, a kliens „szóközt" kiáltott.
 * (A másik öt eltérésben a kliens volt a lazább — `Aa1漢漢漢`, `½`, `ʰ`, `Ⅷ`, `U+0085` —, ami
 * az invariánssal nem ütközik, de most ezek közül is négy megszűnik.)
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * KÉT ÁG: AZONOS LOGIKA, HA A FUTTATÓ TUDJA — LAZÁBB, HA NEM
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * A `\p{...}` (Unicode property escape) ES2018-as, és a regexet a `frontend/tsconfig.json`
 * `es5` célja miatt LITERÁLBAN nem írhatjuk le (a TypeScript változatlanul továbbadná, tehát
 * egy régi futtatón SZINTAKTIKAI hiba, azaz fehér képernyő lenne). Ezért:
 *   (a) a mintákat STRINGBŐL építjük, `new RegExp(..., 'u')`-val, egy `try/catch` őrrel a
 *       modul betöltésekor. Ha a futtató támogatja (minden mai böngésző), a logika a
 *       szerverrel AZONOS;
 *   (b) ha NEM támogatja, a tartalék ág LAZÁBB, nem szigorúbb: az ASCII karaktereket pontosan
 *       osztályozza (ott nincs kétértelműség), MINDEN nem-ASCII, nem-szóköz karakter viszont
 *       „joker": kielégíti a kisbetű/nagybetű/számjegy/nem-betű-szám feltételek MINDEGYIKÉT.
 *       Így a tartalék ág SOHA nem utasít el olyat, amit a szerver elfogad.
 * A szóköz készlete MINDKÉT ágban a .NET `\s` PONTOS listája (lásd `NET_WHITESPACE_CLASS`),
 * nem a JS `\s` — épp ez volt az egyik mért eltérés.
 *
 * ⚠️ A `mobile/utils/passwordPolicy.ts` ugyanezt a szabályt tartalmazza a mobilhoz. A két
 * fájl tesztje ugyanazt a 7 mért esetet is végigfuttatja; a magyar mondat egyezését a
 * szerver FORRÁSFÁJLJÁBÓL olvasva ellenőrzi (forrás-őr), hogy ne tudjon elcsúszni.
 */

export const PASSWORD_MIN_LENGTH = 6;

/**
 * A .NET `\s` PONTOS készlete karakter-osztályként: `[\f\n\r\t\v\x85\p{Z}]`, ahol a `\p{Z}`
 * (Zs/Zl/Zp) felsorolva. ⚠️ A JS `\s`-t SZÁNDÉKOSAN nem használjuk: az illeszti a `U+FEFF`-et
 * (a .NET nem), és NEM illeszti a `U+0085`-öt (a .NET igen) — mindkettő mért eltérés volt.
 */
const NET_WHITESPACE_CLASS =
  '\\t\\n\\v\\f\\r \\u0085\\u00a0\\u1680\\u2000-\\u200a\\u2028\\u2029\\u202f\\u205f\\u3000';

const NET_WHITESPACE = new RegExp('[' + NET_WHITESPACE_CLASS + ']');

/**
 * Támogatja-e a futtató a `\p{...}`-t az `u` flaggel? A próbát a modul betöltésekor EGYSZER
 * végezzük el; a nem támogatott minta `new RegExp`-ben KIVÉTELT dob, nem szintaktikai hibát,
 * tehát elkapható — ezért nem írunk regex-LITERÁLT.
 */
export const UNICODE_PROPERTY_ESCAPES_SUPPORTED: boolean = (() => {
  try {
    return new RegExp('\\p{Ll}', 'u').test('a');
  } catch (e) {
    return false;
  }
})();

export interface PasswordChecks {
  hasLowerCase: (value: string) => boolean;
  hasUpperCase: (value: string) => boolean;
  hasDigit: (value: string) => boolean;
  hasNonAlphanumeric: (value: string) => boolean;
  hasWhitespace: (value: string) => boolean;
}

/**
 * A részfeltételek a megadott ág szerint. EXPORTÁLT, mert a TARTALÉK ágat is mérni kell:
 * a Jest Node-on fut, ott a `\p{...}` MŰKÖDIK, tehát a tartalék ág alapból SOHA nem futna
 * le tesztben — az pedig hamis zöld lenne.
 */
export const buildPasswordChecks = (unicodeSupported: boolean): PasswordChecks => {
  if (unicodeSupported) {
    // A SZERVERREL AZONOS LOGIKA: ugyanazok az Unicode-kategóriák, mint a .NET mintában
    // (`\d` a .NET-ben = `\p{Nd}`).
    const lower = new RegExp('\\p{Ll}', 'u');
    const upper = new RegExp('\\p{Lu}', 'u');
    const digit = new RegExp('\\p{Nd}', 'u');
    const nonAlnum = new RegExp('[^\\p{L}\\p{N}' + NET_WHITESPACE_CLASS + ']', 'u');

    return {
      hasLowerCase: (value: string) => lower.test(value),
      hasUpperCase: (value: string) => upper.test(value),
      hasDigit: (value: string) => digit.test(value),
      hasNonAlphanumeric: (value: string) => nonAlnum.test(value),
      hasWhitespace: (value: string) => NET_WHITESPACE.test(value),
    };
  }

  // ══ TARTALÉK ÁG (ES2018 nélküli futtató): LAZÁBB, DE SOHA NEM SZIGORÚBB ══
  // Az ASCII rész pontosan ugyanaz, mint a szerveren (`a-z` = Ll, `A-Z` = Lu, `0-9` = Nd, a
  // többi ASCII nem betű/szám). Ami NEM ASCII, azt ASCII-eszközökkel nem tudjuk biztosan
  // osztályozni (van Ll nagybetű-párosítás nélkül, van betűnek látszó szimbólum, van
  // nem-ASCII számjegy), ezért JOKERNEK vesszük: MINDEN feltételt kielégíthet. Ez a
  // legrosszabb esetben átenged egy jelszót a szerverhez, ami ott 400-at kap a teljes
  // szabályt kimondó magyar mondattal — a fordítottja, a kliensen való elakadás, a káros.
  const joker = (ch: string) => ch.charCodeAt(0) > 0x7f && !NET_WHITESPACE.test(ch);

  const some = (value: string, pred: (ch: string) => boolean) => {
    for (let i = 0; i < value.length; i++) {
      if (pred(value.charAt(i))) return true;
    }
    return false;
  };

  const asciiLower = (ch: string) => ch >= 'a' && ch <= 'z';
  const asciiUpper = (ch: string) => ch >= 'A' && ch <= 'Z';
  const asciiDigit = (ch: string) => ch >= '0' && ch <= '9';

  return {
    hasLowerCase: (value: string) => some(value, (ch) => asciiLower(ch) || joker(ch)),
    hasUpperCase: (value: string) => some(value, (ch) => asciiUpper(ch) || joker(ch)),
    hasDigit: (value: string) => some(value, (ch) => asciiDigit(ch) || joker(ch)),
    hasNonAlphanumeric: (value: string) =>
      some(
        value,
        (ch) =>
          joker(ch) ||
          (!asciiLower(ch) && !asciiUpper(ch) && !asciiDigit(ch) && !NET_WHITESPACE.test(ch))
      ),
    hasWhitespace: (value: string) => NET_WHITESPACE.test(value),
  };
};

const checks = buildPasswordChecks(UNICODE_PROPERTY_ESCAPES_SUPPORTED);

/** Van-e kisbetű (a .NET `\p{Ll}` — az ékezetes `ó` is kisbetű). */
export const hasLowerCase = (value: string): boolean => checks.hasLowerCase(value);

/** Van-e nagybetű (a .NET `\p{Lu}` — az `Ó` is). */
export const hasUpperCase = (value: string): boolean => checks.hasUpperCase(value);

/** Van-e számjegy (a .NET `\d` = `\p{Nd}`, tehát a nem-ASCII számjegy IS). */
export const hasDigit = (value: string): boolean => checks.hasDigit(value);

/** Van-e NEM betű/szám (és nem szóköz) karakter — ez a „speciális karakter" bő olvasata. */
export const hasNonAlphanumeric = (value: string): boolean => checks.hasNonAlphanumeric(value);

/** Van-e szóköz — a .NET `\s` készlete szerint (NEM a JS `\s` szerint). */
export const hasWhitespace = (value: string): boolean => checks.hasWhitespace(value);

/**
 * A szerver mondatával SZÓ SZERINT egyező, teljes szabály-leírás — a
 * `PasswordPolicy.Message` másolata [K-7, 2026-09-16].
 *
 * ⚠️ ITT KORÁBBAN MÁS MONDAT ÁLLT („A jelszónak tartalmaznia kell…"), miközben a doksi azt
 * írta, hogy szó szerint egyezik a szerverrel. A felhasználó így ugyanarra a hibára KÉT
 * különböző mondatot láthatott (a klienstől és a szervertől) — ez a fajta eltérés az, amiből
 * a támogatás azt hiszi, hogy két külön hiba van. A két szöveg egyezését teszt őrzi, amely a
 * szerver FORRÁSFÁJLJÁBÓL olvassa ki a mondatot.
 */
export const PASSWORD_RULE_MESSAGE =
  'A jelszó legalább 6 karakter, és tartalmaznia kell kisbetűt, nagybetűt, számot és '
  + 'legalább egy nem betű/szám karaktert (pl. ! ? - _ .); szóköz nem lehet benne.';

/**
 * UGYANAZ A SZABÁLY, „az új jelszó" MEGSZÓLÍTÁSSAL — a szerver `PasswordPolicy.NewPasswordMessage`
 * szó szerinti másolata [B-W7, 2026-09-16].
 *
 * MIÉRT KELL KÜLÖN KONSTANS: a szerver a jelszó-CSERE és a visszaállítás útján ezt a mondatot
 * adja, nem a fentit. Ha a kliens a „create"-mondatot írná ki ugyanarra a hibára, a felhasználó
 * KÉT különböző szöveget látna (a mezőnél az egyiket, a szerver válaszából a másikat) — épp az a
 * fajta eltérés, amiből a támogatás két külön hibát hisz. Az egyezést itt is forrás-őr védi.
 */
export const NEW_PASSWORD_RULE_MESSAGE =
  'Az új jelszó legalább 6 karakter, és tartalmaznia kell kisbetűt, nagybetűt, számot és '
  + 'legalább egy nem betű/szám karaktert (pl. ! ? - _ .); szóköz nem lehet benne.';

/**
 * A jelszó hibája MAGYARUL, vagy `null`, ha megfelel. A hossz-hibát adjuk először: a
 * felhasználó azt tudja a leggyorsabban javítani, és a rövid jelszónál az erősség-mondat
 * csak zavar.
 */
export const passwordPolicyError = (password: string): string | null => {
  if (!password) return 'A jelszó megadása kötelező.';
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `A jelszó legalább ${PASSWORD_MIN_LENGTH} karakter legyen.`;
  }
  if (hasWhitespace(password)) return 'A jelszó nem tartalmazhat szóközt.';
  if (!hasLowerCase(password) || !hasUpperCase(password) || !hasDigit(password)
      || !hasNonAlphanumeric(password)) {
    return PASSWORD_RULE_MESSAGE;
  }
  return null;
};

/**
 * Ugyanaz az ellenőrzés, de a jelszó-CSERE/visszaállítás megszólításával [B-W7, 2026-09-16].
 * Szándékosan a `passwordPolicyError`-ra épül: EGY szabály van, csak a megszólítás más — így a
 * két út nem tud elcsúszni egymástól.
 */
export const newPasswordPolicyError = (password: string): string | null => {
  if (!password) return 'Az új jelszó megadása kötelező.';
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Az új jelszó legalább ${PASSWORD_MIN_LENGTH} karakter legyen.`;
  }
  const error = passwordPolicyError(password);
  if (!error) return null;
  return error === PASSWORD_RULE_MESSAGE
    ? NEW_PASSWORD_RULE_MESSAGE
    : error.replace('A jelszó ', 'Az új jelszó ');
};
